import React, { useState, useCallback, useEffect } from 'react';
import Typography from '../../../basic/Typography';
import styles from '../NewNG.module.css';

interface VideoControlsProps {
  showControls: boolean;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  onToggleOrientation: () => void;
  onResetOrientation: () => void;
  isMuted: boolean;
  volume: number;
  orientation: 'portrait' | 'landscape';
  manualMode: 'portrait' | 'landscape' | null;
  isPaused: boolean;
  currentJobId?: string;
}

export const VideoControls: React.FC<VideoControlsProps> = ({
  showControls,
  onTogglePlay,
  onToggleMute,
  onVolumeChange,
  onToggleOrientation,
  onResetOrientation,
  isMuted,
  volume,
  orientation,
  manualMode,
  isPaused,
  currentJobId
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [sliderRef, setSliderRef] = useState<HTMLDivElement | null>(null);

  // Helper function to calculate volume from position
  const calculateVolumeFromPosition = useCallback((clientX: number) => {
    if (!sliderRef) return volume;
    const rect = sliderRef.getBoundingClientRect();
    const clickX = clientX - rect.left;
    return Math.max(0, Math.min(1, clickX / rect.width));
  }, [sliderRef, volume]);

  // Mouse events
  const handleVolumeClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) {
      const newVolume = calculateVolumeFromPosition(e.clientX);
      onVolumeChange(newVolume);
    }
  }, [isDragging, calculateVolumeFromPosition, onVolumeChange]);

  const handleVolumeMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && sliderRef) {
      const newVolume = calculateVolumeFromPosition(e.clientX);
      onVolumeChange(newVolume);
    }
  }, [isDragging, calculateVolumeFromPosition, onVolumeChange, sliderRef]);

  const handleGlobalMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for mobile
  const handleVolumeTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleVolumeTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging && sliderRef) {
      const touch = e.touches[0];
      const newVolume = calculateVolumeFromPosition(touch.clientX);
      onVolumeChange(newVolume);
    }
  }, [isDragging, calculateVolumeFromPosition, onVolumeChange, sliderRef]);

  const handleVolumeTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global touch events for dragging outside the slider
  const handleGlobalTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && sliderRef) {
      const touch = e.touches[0];
      const newVolume = calculateVolumeFromPosition(touch.clientX);
      onVolumeChange(newVolume);
    }
  }, [isDragging, calculateVolumeFromPosition, onVolumeChange, sliderRef]);

  const handleGlobalTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Phone volume button integration
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle volume up/down keys (including phone volume buttons on some devices)
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const currentVolume = volume;
        const volumeStep = 0.1;

        if (e.key === 'ArrowUp') {
          const newVolume = Math.min(1, currentVolume + volumeStep);
          onVolumeChange(newVolume);
        } else if (e.key === 'ArrowDown') {
          const newVolume = Math.max(0, currentVolume - volumeStep);
          onVolumeChange(newVolume);
        }
      }
    };

    // MediaSession API for better mobile integration
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        // Could be used for previous video
      });

      navigator.mediaSession.setActionHandler('nexttrack', () => {
        // Could be used for next video
      });

      navigator.mediaSession.setActionHandler('play', () => {
        // Could be used for play/pause
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        // Could be used for play/pause
      });
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [volume, onVolumeChange]);

  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      // Touch events
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUp, handleGlobalTouchMove, handleGlobalTouchEnd]);

  const volumePercentage = isMuted ? 0 : volume * 100;

    return (
    <div
      className={`${styles['newng-video-overlay']} ${showControls ? styles.show : styles.hide}`}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      <div className={styles['newng-video-controls']}>
        <button className={styles['newng-control-button']} onClick={onTogglePlay}>
          {isPaused ? '▶\uFE0E' : '⏸\uFE0E'}
        </button>
        <div className={styles['newng-volume-control']}>
          <Typography variant="news-meta" component="span" className={styles['newng-volume-level']}>{Math.round(volumePercentage)}%</Typography>
          <div
            ref={setSliderRef}
            className={styles['newng-volume-slider']}
            onClick={handleVolumeClick}
            onMouseDown={handleVolumeMouseDown}
            onTouchStart={handleVolumeTouchStart}
            onTouchMove={handleVolumeTouchMove}
            onTouchEnd={handleVolumeTouchEnd}
          >
            <div
              className={styles['newng-volume-fill']}
              style={{ width: `${volumePercentage}%` }}
            ></div>
            <div
              className={styles['newng-volume-handle']}
              style={{ left: `${volumePercentage}%` }}
            ></div>
          </div>
          <button className={styles['newng-volume-button']} onClick={onToggleMute}>
            <span style={{ filter: 'grayscale(100%)' }}>{isMuted ? '🔊︎\uFE0E' : '🔇︎\uFE0E'}</span>
          </button>
        </div>
        <button className={styles['newng-control-button']} disabled title="Fullscreen disabled in theatre mode">
          ⛶︎
        </button>
        <div className={styles['newng-orientation-controls']}>
          <button
            className={`${styles['newng-orientation-button']} ${orientation === 'portrait' ? styles['portrait-active'] : styles['landscape-active']}`}
            onClick={onToggleOrientation}
            title={`Switch to ${orientation === 'portrait' ? 'Landscape' : 'Portrait'}`}
          >
            <span style={{ filter: 'grayscale(100%)' }}>📱</span>
          </button>
        </div>
        {currentJobId && (
          <button
            className={`${styles['newng-control-button']} ${styles['newng-theatre-mode-button']}`}
            onClick={() => {
              // Toggle between NewNG and NewNG Theatre mode
              const path = window.location.pathname;
              const isTheatre = path.includes('/newsng-theatre');
              window.location.href = isTheatre
                ? '/game/newsng'
                : `/game/newsng-theatre?jobId=${currentJobId}`;
              }}
            title="Toggle theatre mode"
          >
            🎬
          </button>
        )}
      </div>
    </div>
  );
};